import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { businessStatesApi } from '@/lib/api';
import { toast } from 'sonner';
import { Plus, MapPin, Trash2, Settings2 } from 'lucide-react';
import { motion } from 'framer-motion';

const US_STATES = [
  { code: 'AL', name: 'Alabama' }, { code: 'AK', name: 'Alaska' }, { code: 'AZ', name: 'Arizona' },
  { code: 'AR', name: 'Arkansas' }, { code: 'CA', name: 'California' }, { code: 'CO', name: 'Colorado' },
  { code: 'CT', name: 'Connecticut' }, { code: 'DE', name: 'Delaware' }, { code: 'FL', name: 'Florida' },
  { code: 'GA', name: 'Georgia' }, { code: 'HI', name: 'Hawaii' }, { code: 'ID', name: 'Idaho' },
  { code: 'IL', name: 'Illinois' }, { code: 'IN', name: 'Indiana' }, { code: 'IA', name: 'Iowa' },
  { code: 'KS', name: 'Kansas' }, { code: 'KY', name: 'Kentucky' }, { code: 'LA', name: 'Louisiana' },
  { code: 'ME', name: 'Maine' }, { code: 'MD', name: 'Maryland' }, { code: 'MA', name: 'Massachusetts' },
  { code: 'MI', name: 'Michigan' }, { code: 'MN', name: 'Minnesota' }, { code: 'MS', name: 'Mississippi' },
  { code: 'MO', name: 'Missouri' }, { code: 'MT', name: 'Montana' }, { code: 'NE', name: 'Nebraska' },
  { code: 'NV', name: 'Nevada' }, { code: 'NH', name: 'New Hampshire' }, { code: 'NJ', name: 'New Jersey' },
  { code: 'NM', name: 'New Mexico' }, { code: 'NY', name: 'New York' }, { code: 'NC', name: 'North Carolina' },
  { code: 'ND', name: 'North Dakota' }, { code: 'OH', name: 'Ohio' }, { code: 'OK', name: 'Oklahoma' },
  { code: 'OR', name: 'Oregon' }, { code: 'PA', name: 'Pennsylvania' }, { code: 'RI', name: 'Rhode Island' },
  { code: 'SC', name: 'South Carolina' }, { code: 'SD', name: 'South Dakota' }, { code: 'TN', name: 'Tennessee' },
  { code: 'TX', name: 'Texas' }, { code: 'UT', name: 'Utah' }, { code: 'VT', name: 'Vermont' },
  { code: 'VA', name: 'Virginia' }, { code: 'WA', name: 'Washington' }, { code: 'WV', name: 'West Virginia' },
  { code: 'WI', name: 'Wisconsin' }, { code: 'WY', name: 'Wyoming' }, { code: 'DC', name: 'Washington D.C.' },
];

export default function SettingsPage() {
  const [businessStates, setBusinessStates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedState, setSelectedState] = useState(null);
  const [formData, setFormData] = useState({
    state_code: '',
    state_name: '',
    is_active: true,
  });

  useEffect(() => {
    loadBusinessStates();
  }, []);

  const loadBusinessStates = async () => {
    try {
      const response = await businessStatesApi.getAll();
      setBusinessStates(response.data);
    } catch (error) {
      toast.error('Failed to load business states');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = () => {
    setFormData({ state_code: '', state_name: '', is_active: true });
    setDialogOpen(true);
  };

  const handleStateChange = (stateCode) => {
    const state = US_STATES.find((s) => s.code === stateCode);
    setFormData({
      ...formData,
      state_code: stateCode,
      state_name: state?.name || '',
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await businessStatesApi.create(formData);
      toast.success('Business state added');
      setDialogOpen(false);
      loadBusinessStates();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add state');
    }
  };

  const handleToggleActive = async (state) => {
    try {
      await businessStatesApi.update(state.id, !state.is_active);
      toast.success(`State ${state.is_active ? 'deactivated' : 'activated'}`);
      loadBusinessStates();
    } catch (error) {
      toast.error('Failed to update state');
    }
  };

  const handleDelete = async () => {
    if (!selectedState) return;
    try {
      await businessStatesApi.delete(selectedState.id);
      toast.success('Business state removed');
      setDeleteDialogOpen(false);
      setSelectedState(null);
      loadBusinessStates();
    } catch (error) {
      toast.error('Failed to delete state');
    }
  };

  // Get list of states not yet added
  const availableStates = US_STATES.filter(
    (us) => !businessStates.some((bs) => bs.state_code === us.code)
  );

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.05 } },
  };

  const itemVariants = {
    hidden: { opacity: 0, x: -20 },
    visible: { opacity: 1, x: 0 },
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="settings-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Settings</h1>
        <p className="text-muted-foreground mt-1">
          Configure your CityWorth census tool
        </p>
      </div>

      {/* Business States */}
      <Card className="bg-card border-border" data-testid="business-states-card">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-lg flex items-center gap-2">
                <MapPin className="w-5 h-5 text-primary" />
                Business States
              </CardTitle>
              <CardDescription>
                States where CityWorth does business. Used for mailing list filtering.
              </CardDescription>
            </div>
            <Button
              onClick={handleOpenDialog}
              disabled={availableStates.length === 0}
              className="bg-[#E31837] hover:bg-[#c41530] text-white"
              data-testid="add-state-button"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add State
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {businessStates.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <MapPin className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>No business states configured yet.</p>
              <p className="text-sm">Add states where CityWorth operates.</p>
            </div>
          ) : (
            <motion.div
              variants={containerVariants}
              initial="hidden"
              animate="visible"
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3"
            >
              {businessStates.map((state) => (
                <motion.div
                  key={state.id}
                  variants={itemVariants}
                  className={`flex items-center justify-between p-3 rounded-lg border ${
                    state.is_active
                      ? 'border-primary/30 bg-primary/5'
                      : 'border-border bg-muted/30'
                  }`}
                  data-testid={`state-item-${state.state_code}`}
                >
                  <div className="flex items-center gap-3">
                    <div
                      className={`w-10 h-10 rounded-lg flex items-center justify-center text-sm font-bold ${
                        state.is_active
                          ? 'bg-primary/20 text-primary'
                          : 'bg-muted text-muted-foreground'
                      }`}
                    >
                      {state.state_code}
                    </div>
                    <div>
                      <p className="font-medium text-sm">{state.state_name}</p>
                      <p className="text-xs text-muted-foreground">
                        {state.is_active ? 'Active' : 'Inactive'}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={state.is_active}
                      onCheckedChange={() => handleToggleActive(state)}
                      data-testid={`toggle-state-${state.state_code}`}
                    />
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => {
                        setSelectedState(state);
                        setDeleteDialogOpen(true);
                      }}
                      data-testid={`delete-state-${state.state_code}`}
                    >
                      <Trash2 className="w-4 h-4 text-destructive" />
                    </Button>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          )}
          <p className="text-xs text-muted-foreground mt-4">
            {businessStates.filter((s) => s.is_active).length} active state(s) out of{' '}
            {businessStates.length} total
          </p>
        </CardContent>
      </Card>

      {/* Add State Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-card border-border" data-testid="add-state-dialog">
          <DialogHeader>
            <DialogTitle>Add Business State</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label>Select State</Label>
              <Select value={formData.state_code} onValueChange={handleStateChange}>
                <SelectTrigger className="bg-background border-input" data-testid="state-select">
                  <SelectValue placeholder="Choose a state" />
                </SelectTrigger>
                <SelectContent>
                  {availableStates.map((state) => (
                    <SelectItem key={state.code} value={state.code}>
                      {state.name} ({state.code})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={!formData.state_code}
                className="bg-[#E31837] hover:bg-[#c41530] text-white"
                data-testid="save-state-button"
              >
                Add State
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="bg-card border-border">
          <AlertDialogHeader>
            <AlertDialogTitle>Remove Business State</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to remove {selectedState?.state_name} from your business
              states? This won't affect existing employee data.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive hover:bg-destructive/90"
              data-testid="confirm-delete-state"
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
